import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { publicApi } from '../config/api';
import Layout from './Layout';
import { formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

interface PaymentTransaction {
  transaction_id: string;
  order_number: string;
  payment_date: string;
  payment_method: string;
  base_amount: number;
  shipping_amount: number;
  tax_amount: number;
  fee_amount: number;
  total_amount: number;
  status: string;
  ach_expected_clearing_date?: string;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<PaymentTransaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGuestPayment, setIsGuestPayment] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      loadTransactionData();
    } else {
      setError('No payment session found');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadTransactionData = async (retryCount = 0) => {
    if (!sessionId) {
      setError('No payment session found');
      setLoading(false);
      return;
    }

    let shouldStopLoading = true;

    try {
      // Use publicApi for session status (works for both auth and guest)
      const statusResponse = await publicApi.get(`/payments/session-status/${sessionId}`);
      console.log('Session status:', statusResponse.data);

      // Check if this is a guest payment
      if (statusResponse.data.from_guest === 'true' || statusResponse.data.from_guest === true) {
        setIsGuestPayment(true);
      }

      // Treat anything except 'failed' as success (including pending, complete, processing, etc.)
      if (statusResponse.data.status !== 'failed') {
        // Check if order_id exists yet
        if (!statusResponse.data.order_id) {
          console.log('Order not created yet by webhook, retrying...', retryCount);
          // If order doesn't exist yet and we haven't retried too many times, retry after delay
          if (retryCount < 5) {
            // Keep loading state active while retrying (reduced to 5 retries)
            shouldStopLoading = false;
            setTimeout(() => loadTransactionData(retryCount + 1), 1000); // Retry after 1 second
            return;
          } else {
            // After 5 retries, show success message
            // NON-FAILED = SUCCESS (per user requirement)
            console.log('Showing success - payment was successful, order details being processed');

            // Clear the cart (only for authenticated users)
            if (user) {
              try {
                await api.post('/cart/clear');
                window.dispatchEvent(new Event('cartUpdated'));
              } catch (e) {
                console.error('Failed to clear cart:', e);
              }
            }

            // Show simple success without transaction details
            setError('success_no_details');
            return;
          }
        }

        // Load the order details for display
        // For guest payments, use public API directly
        let orderResponse;
        try {
          if (statusResponse.data.from_guest === 'true' || statusResponse.data.from_guest === true) {
            // Guest order - use public API
            orderResponse = await publicApi.get(`/orders/${statusResponse.data.order_id}`);
          } else {
            // Authenticated order - try authenticated API first
            orderResponse = await api.get(`/orders/${statusResponse.data.order_id}`);
          }
        } catch (authError: any) {
          // If authenticated request fails with 401 or 403, try public API as fallback
          if (authError.response?.status === 401 || authError.response?.status === 403) {
            orderResponse = await publicApi.get(`/orders/${statusResponse.data.order_id}`);
          } else {
            throw authError;
          }
        }
        const orderData = orderResponse.data.order || orderResponse.data;

        // Get payment method from session-status response (includes metadata from Stripe)
        const paymentMethod = statusResponse.data.payment_method || 'card';

        // Create transaction data from order
        setTransaction({
          transaction_id: sessionId,
          order_number: orderData.order_number || statusResponse.data.order_number,
          payment_date: new Date().toISOString(),
          payment_method: paymentMethod,
          base_amount: orderData.subtotal || orderData.total_amount,
          shipping_amount: orderData.shipping_cost || 0,
          tax_amount: orderData.tax_amount || 0,
          fee_amount: orderData.convenience_fee || 0,
          total_amount: orderData.total_amount,
          status: 'completed',
          ach_expected_clearing_date: undefined
        });

        // Clear the cart immediately on success (only for authenticated users)
        if (user) {
          try {
            await api.post('/cart/clear');
            console.log('Cart cleared after successful payment');
            // Notify Layout to update cart badge immediately
            window.dispatchEvent(new Event('cartUpdated'));
          } catch (cartError) {
            console.error('Failed to clear cart:', cartError);
            // Don't show error to user, cart will be cleared eventually
          }
        }
      } else {
        // Payment failed
        setError(statusResponse.data.message || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Failed to load transaction:', error);
      setError('Unable to load payment details');
    } finally {
      if (shouldStopLoading) {
        setLoading(false);
      }
    }
  };

  // Render anonymous header for guest payments
  const renderAnonymousHeader = () => (
    <div style={{
      background: '#4D64C1',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 1040,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <a href="https://www.purehd.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/img/PureHD_Logo.png"
                alt="PureHD"
                style={{
                  height: '45px',
                  filter: 'brightness(0) invert(1)'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </a>
          </div>
          <div className="text-center">
            <span style={{
              color: 'white',
              fontSize: '16px'
            }}>
              <i className="fas fa-phone-alt me-2"></i>
              <span className="d-none d-sm-inline">(888) 787-3431</span>
            </span>
          </div>
          <div>
            <button
              className="btn btn-outline-light"
              onClick={() => navigate('/login')}
              style={{ fontSize: '14px' }}
            >
              <i className="fas fa-sign-in-alt me-2"></i>
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const wrapContent = (content: React.ReactNode) => {
    if (isGuestPayment) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
          {renderAnonymousHeader()}
          <div className="flex-grow-1" style={{ padding: '30px 0' }}>
            {content}
          </div>
        </div>
      );
    }
    return <Layout>{content}</Layout>;
  };

  if (loading) {
    return wrapContent(
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading payment details...</span>
          </div>
          <p className="mt-3">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Special case: Success without order details (NON-FAILED = SUCCESS)
    if (error === 'success_no_details') {
      return wrapContent(
        <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-md-8 col-lg-6">
                <div className="card shadow">
                  <div className="card-body text-center py-5">
                    <div className="mb-4">
                      <i className="fas fa-check-circle text-success fa-4x"></i>
                    </div>
                    <h2 className="mb-3">Payment Successful!</h2>
                    <p className="lead mb-4">
                      Your payment has been successfully processed. Your order is being finalized.
                    </p>
                    <div className="alert alert-info text-start mb-4" role="alert">
                      <i className="fas fa-info-circle me-2"></i>
                      Your order details will appear in your Order History within a few moments.
                    </div>
                    <div className="d-grid gap-2">
                      {isGuestPayment ? (
                        <Link to="/login" className="btn btn-primary btn-lg">
                          Login to View Order
                        </Link>
                      ) : (
                        <>
                          <Link to="/dashboard" className="btn btn-primary btn-lg">
                            Return to Dashboard
                          </Link>
                          <Link to="/orders" className="btn btn-outline-secondary btn-lg">
                            View Your Orders
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      );
    }

    // Determine if this is a payment failure or unknown status
    const isPaymentFailed = error.toLowerCase().includes('failed') ||
                           error.toLowerCase().includes('declined') ||
                           error.toLowerCase().includes('could not be located') ||
                           error.toLowerCase().includes('invalid');

    return wrapContent(
      <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="card text-center">
                <div className="card-body py-5">
                  {isPaymentFailed ? (
                    <>
                      <i className="fas fa-times-circle text-danger fa-3x mb-4"></i>
                      <h3 className="text-danger mb-3">Payment Could Not Be Processed</h3>
                      <p className="text-muted mb-2" style={{fontSize: '0.95rem'}}>{error}</p>
                      <p className="mb-4" style={{fontSize: '0.95rem'}}>Please contact your financial institution or try an alternative payment method.</p>

                      <div className="border rounded p-4 mb-4" style={{backgroundColor: '#f8f9fa', maxWidth: '500px', margin: '0 auto'}}>
                        <h5 className="mb-3" style={{fontSize: '1rem', fontWeight: 600}}>Need Assistance?</h5>
                        <div className="mb-3">
                          <i className="fas fa-phone-alt text-primary me-2"></i>
                          <span>Call us at </span>
                          <span className="fw-bold text-dark">(888) 787-3431</span>
                        </div>
                        <div>
                          <i className="fas fa-envelope text-primary me-2"></i>
                          <span>Email us at </span>
                          <a href="mailto:support@purehd.com" className="text-decoration-none fw-bold text-dark">support@purehd.com</a>
                        </div>
                      </div>

                      <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                        <Link to="/cart" className="btn btn-primary btn-lg px-4" style={{minWidth: '200px'}}>
                          Return to Cart
                        </Link>
                        <Link to="/orders" className="btn btn-outline-secondary btn-lg px-4" style={{minWidth: '200px'}}>
                          View Order History
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-exclamation-circle text-warning fa-3x mb-3"></i>
                      <h4>Payment Status Pending</h4>
                      <p className="text-muted mt-3">{error}</p>
                      <p className="mt-3">If you have questions about your payment, please contact our support team at <strong>(888) 787-3431</strong>.</p>
                      <div className="mt-4">
                        <Link to="/orders" className="btn btn-primary">
                          View Order History
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
    );
  }

  const isACH = transaction?.payment_method?.toLowerCase() === 'ach';

  return wrapContent(
    <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card shadow">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="fas fa-check-circle text-success fa-4x"></i>
                </div>

                <h2 className="mb-3">
                  Payment Successful!
                </h2>
                
                {transaction && (
                  <>
                    <p className="lead mb-4">
                      Your payment has been successfully processed.
                    </p>

                    <div className="bg-light rounded p-4 mb-4">
                      <h5 className="mb-4 text-center">Payment Details</h5>
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="fw-semibold text-start" style={{ width: '40%' }}>Transaction ID:</td>
                            <td className="text-end text-break" style={{ fontSize: '0.85rem' }}>{transaction.transaction_id}</td>
                          </tr>
                          <tr>
                            <td className="fw-semibold text-start">Order Number:</td>
                            <td className="text-end">{transaction.order_number}</td>
                          </tr>
                          <tr>
                            <td className="fw-semibold text-start">Payment Date:</td>
                            <td className="text-end">
                              {new Date(transaction.payment_date).toLocaleDateString()}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-semibold text-start">Payment Method:</td>
                            <td className="text-end">
                              {isACH ? 'ACH Bank Transfer' : 'Credit/Debit Card'}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-start">Total Paid:</td>
                            <td className="fw-bold text-primary text-end" style={{ fontSize: '1.25rem' }}>
                              {formatCurrency(transaction.total_amount)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {isACH && transaction.ach_expected_clearing_date && (
                      <div className="alert alert-info text-start" role="alert">
                        <h6 className="alert-heading">
                          <i className="fas fa-info-circle me-2"></i>
                          ACH Payment Processing
                        </h6>
                        <p className="mb-2">
                          Your ACH payment will typically clear within 3-5 business days.
                        </p>
                        <p className="mb-0">
                          <strong>Expected clearing date:</strong>{' '}
                          {new Date(transaction.ach_expected_clearing_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}

                    <div className="alert alert-success text-start" role="alert">
                      <i className="fas fa-envelope me-2"></i>
                      A payment confirmation has been sent to your email address.
                    </div>

                    <div className="d-grid gap-2">
                      {isGuestPayment ? (
                        <Link to="/login" className="btn btn-primary btn-lg">
                          Login to View Order
                        </Link>
                      ) : (
                        <>
                          <Link to="/dashboard" className="btn btn-primary btn-lg">
                            Return to Dashboard
                          </Link>
                          <Link to="/orders" className="btn btn-outline-secondary btn-lg">
                            View Your Orders
                          </Link>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default PaymentSuccess;